import { RectIconV2 } from '@/components/IconButton/RectIconV2';
import { ANALYTICS_EVENT_OBJECT } from '@/constants/analytics';
import { useExplore } from '@/stores/explore';
import { startDraw2D, startDrawRect2D, stopDraw2D, useViewer } from '@/stores/viewer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { cn, inverted, Tooltip, TooltipContent, TooltipTrigger } from '@skand/ui';

export const DrawToolMenuV2 = () => {
  const targetPhoto = useViewer(state => state.targetPhoto);
  const enabledDraw2D = useViewer(state => state.enabledDraw2D);
  const enabledDraw3D = useViewer(state => state.enabledDraw3D);
  const annotationDraft = useExplore(state => state.annotationDraft);

  const toggleFreeDrawTool = () => {
    if (enabledDraw2D !== 'free') {
      startDraw2D();
    } else {
      stopDraw2D();
    }
  };

  const toggleRectDrawTool = () => {
    if (enabledDraw2D !== 'rect') {
      startDrawRect2D();
    } else {
      stopDraw2D();
    }
  };

  const isDisabled =
    targetPhoto === null || enabledDraw3D !== null || annotationDraft?.annotation2d !== undefined;

  if (targetPhoto?.type === 'panorama') return null;

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild disabled={isDisabled}>
          <div className="group/dropdown data-[disabled]:pointer-events-none data-[disabled]:opacity-50">
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'group/tooltip h-8 flex cursor-pointer items-center justify-center gap-1 border-1 border-neutral-100 rounded-6px border-solid p-6px transition-colors',
                  enabledDraw2D !== null
                    ? 'bg-primary-400'
                    : 'bg-neutral-800/20 active:bg-primary-400 hover:bg-primary-500 group-[[data-state=open]]/dropdown:bg-primary-500',
                )}
                data-analytics-event-object={ANALYTICS_EVENT_OBJECT.DRAW_TOOL_3D}
                role="button"
              >
                {enabledDraw2D === 'rect' ? (
                  <RectIconV2 />
                ) : (
                  <div className="i-skand-annotation text-5 color-neutral-100" />
                )}
                <div className="i-skand-dropdown text-6px color-neutral-100 transition-transform group-[[data-state=open]]/dropdown:rotate-180" />
              </div>
            </TooltipTrigger>
          </div>
        </DropdownMenuTrigger>

        <TooltipContent side="top">
          <div className={inverted}>Annotation tools</div>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuPortal>
        <DropdownMenuContent className="mt-2px w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden border-1 border-neutral-100 rounded-6px border-solid">
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem asChild>
                <div
                  className={cn(
                    'w-full h-30px flex cursor-pointer items-center justify-center px-6px outline-none transition-colors',
                    enabledDraw2D === 'free'
                      ? 'bg-primary-400'
                      : 'bg-neutral-800/20 active:bg-primary-400 hover:bg-primary-500',
                  )}
                  onClick={toggleFreeDrawTool}
                  role="button"
                >
                  <div className="i-skand-annotation text-5 color-neutral-100" />
                </div>
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className={inverted}>Pen</div>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem asChild>
                <div
                  className={cn(
                    'w-full h-30px flex cursor-pointer items-center justify-center border-t-1 border-neutral-100 border-t-solid px-6px outline-none transition-colors',
                    enabledDraw2D === 'rect'
                      ? 'bg-primary-400'
                      : 'bg-neutral-800/20 active:bg-primary-400 hover:bg-primary-500',
                  )}
                  onClick={toggleRectDrawTool}
                  role="button"
                >
                  <RectIconV2 />
                </div>
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className={inverted}>Rectangle</div>
            </TooltipContent>
          </Tooltip>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};
